import classNames from "classnames";
import React from "react";

interface RadioGroupOption {
  value: string;
  label: string | JSX.Element;
  description?: string | JSX.Element;
}

interface RadioGroupProps {
  id?: string;
  label?: string | JSX.Element;
  options: RadioGroupOption[];
  selected?: string;
  error?: string | JSX.Element;
  onSelect: (value: string) => void;
  onBlur?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  size?: "sm" | "md";
  variant?: "default" | "compact";
}

export const RadioGroup = ({
  id,
  label,
  options,
  selected,
  error,
  onSelect,
  onBlur,
  size = "md",
  variant = "default",
}: RadioGroupProps) => {
  const isChecked = (option: RadioGroupOption) => {
    return option.value === selected;
  };

  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={typeof label === "string" ? label : undefined}
      className="font-sans w-full space-y-2"
    >
      {label && (
        <div className="flex items-center justify-between">
          <span className="block text-sm font-medium text-primary-900 dark:text-primary-100">
            {label}
          </span>
        </div>
      )}
      <div className="space-y-2">
        {options.map((option, optionIdx) => (
          <div
            id={`${id}-option-${optionIdx}`}
            key={option.value}
            role="radio"
            tabIndex={0}
            aria-checked={isChecked(option)}
            aria-label={
              typeof option.label === "string" ? option.label : undefined
            }
            onClick={(e) => {
              onBlur && onBlur(e);
              onSelect(option.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(option.value);
              }
            }}
            className={classNames(
              "relative flex cursor-pointer items-start rounded-lg border border-solid transition-all duration-200 outline-none",
              "focus-visible:ring-2 focus-visible:ring-primary-500/20 focus-visible:border-primary-500 dark:focus-visible:ring-primary-400/20 dark:focus-visible:border-primary-400",
              isChecked(option)
                ? "border-primary-500 dark:border-primary-400 bg-primary-50/50 dark:bg-primary-900/20 shadow-[0_1px_2px_rgba(167,80,53,0.05)]"
                : "border-primary-200/80 dark:border-primary-800/80 bg-white dark:bg-content-800 hover:border-primary-300 dark:hover:border-primary-700 hover:bg-primary-50/20 dark:hover:bg-primary-950/20",
              size === "sm" ? "p-3" : "p-4",
              variant === "compact" && "py-2",
            )}
          >
            <div className="flex w-full">
              <div
                className={classNames(
                  "flex h-5 items-center",
                  size === "sm" ? "mt-0.5" : "mt-1",
                )}
              >
                <div
                  className={classNames(
                    "flex h-4 w-4 items-center justify-center rounded-full border border-solid bg-white dark:bg-content-800 transition-colors",
                    isChecked(option)
                      ? "border-primary-500 dark:border-primary-400"
                      : "border-primary-300 dark:border-primary-600",
                  )}
                >
                  {isChecked(option) && (
                    <div className="h-2 w-2 rounded-full bg-primary-500 dark:bg-primary-400" />
                  )}
                </div>
              </div>
              <div className="ml-3 flex-1">
                <label
                  className={classNames(
                    "font-medium text-primary-900 dark:text-primary-100",
                    size === "sm" ? "text-sm" : "text-base",
                  )}
                >
                  {option.label}
                </label>
                {option.description && variant !== "compact" && (
                  <p
                    className={classNames(
                      "text-primary-600 dark:text-primary-400",
                      size === "sm" ? "text-xs mt-0.5" : "text-sm mt-1",
                    )}
                  >
                    {option.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {error && (
        <p className="mt-2 text-sm font-medium text-error-600 dark:text-error-400">
          {error}
        </p>
      )}
    </div>
  );
};
